import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AppSettingsContext = createContext(null);

// Scurisce un colore hex (0..1) per hover/gradienti del brand cliente
export const darken = (hex, k = 0.18) => {
  const n = parseInt(hex.slice(1), 16);
  const f = (v) => Math.max(0, Math.round(v * (1 - k))).toString(16).padStart(2, "0");
  return `#${f((n >> 16) & 255)}${f((n >> 8) & 255)}${f(n & 255)}`;
};
export const useClientBrand = () => {
  const { settings } = useAppSettings();
  const brand = /^#[0-9a-fA-F]{6}$/.test(settings.client_brand_color || "") ? settings.client_brand_color : "#1E40AF";
  return { brand, brandDark: darken(brand) };
};

export const DEFAULT_SETTINGS = {
  app_name: "Vivi Fidelity Club",
  logo_url: null,
  primary_color: "#1E40AF",
  accent_color: "#0EA5E9",
  client_brand_color: "#1E40AF",
  card_bg: "#18181C",
  card_bg_image_url: null,
  card_text: "#F4F4F5",
  card_tagline: "Ogni panino ti avvicina al prossimo premio.",
  card_footer: "Presenta questo QR al negozio per accumulare punti.",
  contest_url: null,
  contest_title: "Concorsi",
  mobile_bottom_nav_slots: ["/banco", "/ordini-cliente", "/clienti"],
  botnav_preset_icons: { manager: null, pos: null, marketing: null },
  date_presets: [],
  default_branch_id: null,
  daily_product_enabled: true,
  daily_product_discount_pct: 0,
  daily_product_label: "Prodotto del giorno",
  support_email: "",
};

export function AppSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const load = async () => {
    try {
      const { data } = await api.get(`/settings/app`);
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch { /* keep defaults */ }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    document.title = settings.app_name || "Vivi Fidelity Club";
    document.querySelector('meta[name="apple-mobile-web-app-title"]')?.setAttribute("content", settings.app_name || "Vivi Fidelity Club");
    document.documentElement.classList.add("vivi-light");
    document.documentElement.style.setProperty("--paprika", settings.primary_color);
    document.documentElement.style.setProperty("--paprika-hover", darken(settings.primary_color || "#1E40AF", 0.18));
    document.documentElement.style.setProperty("--gold", settings.accent_color);
    const brand = /^#[0-9a-fA-F]{6}$/.test(settings.client_brand_color || "") ? settings.client_brand_color : "#1E40AF";
    document.documentElement.style.setProperty("--client-brand", brand);
    document.documentElement.style.setProperty("--client-brand-dark", darken(brand, 0.18));
    // Tema esteso per Home/portale cliente (per circuito)
    const hex = (v, d) => (/^#[0-9a-fA-F]{6}$/.test(v || "") ? v : d);
    document.documentElement.style.setProperty("--client-bg", hex(settings.client_bg_color, "#FFFFFF"));
    document.documentElement.style.setProperty("--client-text", hex(settings.client_text_color, "#18181B"));
    const fonts = { default: "", rounded: "'Nunito', 'Manrope', system-ui, sans-serif", serif: "'Playfair Display', Georgia, serif", modern: "'Space Grotesk', 'Manrope', system-ui, sans-serif" };
    document.documentElement.style.setProperty("--client-font", fonts[settings.client_font] || "");
    const radius = { soft: "1.5rem", sharp: "0.5rem", pill: "2rem" };
    document.documentElement.style.setProperty("--client-radius", radius[settings.client_card_style] || "1.5rem");
  }, [settings]);

  return (
    <AppSettingsContext.Provider value={{ settings, setSettings, reload: load }}>
      {children}
    </AppSettingsContext.Provider>
  );
}

export const useAppSettings = () => useContext(AppSettingsContext);

export function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}
